// Phase B — Customer Portal · AI chat intake.
//
// Uses the `customer_portal` AI intake context (server-side). Customer-safe
// — no internal pricing, no celar; the AI can collect a `target_price` if
// the user offers it. On finalize, server creates the deal under the
// customer's linked forwarder and stamps created_via_customer_portal=true.
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Send, Sparkles, ArrowLeft, RotateCw, Check } from "lucide-react";
import { useT } from "../../lib/i18n";
import { useAiIntakeIntro, useAiIntakeChat, useAiIntakeCreate } from "../../api/aiIntake";
import { toast } from "sonner";

const CONTEXT = "customer_portal";

const FIELD_LABEL_KEYS = {
  deal_name: "portal.chat.fld_deal_name",
  pickup: "portal.chat.fld_pickup",
  dropoff: "portal.chat.fld_dropoff",
  cargo: "portal.chat.fld_cargo",
  target_price: "portal.chat.fld_target_price",
  note: "portal.chat.fld_note",
};

const mono = (size, color, weight, ls) => ({
  fontFamily: "'DM Mono', monospace",
  fontSize: size,
  color: color || "var(--text, #f0f2f8)",
  fontWeight: weight || 400,
  letterSpacing: ls,
});

function _fmt(key, value) {
  if (value == null) return "—";
  if (key === "pickup" || key === "dropoff") {
    const c = [value.city, value.country].filter(Boolean).join(", ");
    return c || "—";
  }
  if (key === "cargo") {
    return [value.aru_megnevezese, value.autotipus, value.suly_kg && `${value.suly_kg} kg`]
      .filter(Boolean).join(" · ") || "—";
  }
  return String(value);
}

export default function PortalChat() {
  const navigate = useNavigate();
  const { locale, t } = useT();
  const intro = useAiIntakeIntro(CONTEXT, locale);
  const chat = useAiIntakeChat(CONTEXT);
  const createDraft = useAiIntakeCreate(CONTEXT);

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [extracted, setExtracted] = useState({});
  const [missing, setMissing] = useState([]);
  const [minimumMet, setMinimumMet] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (intro.data?.intro && messages.length === 0) {
      setMessages([{ role: "assistant", content: intro.data.intro }]);
    }
  }, [intro.data?.intro]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const _createWith = async (extr) => {
    try {
      const created = await createDraft.mutateAsync(extr);
      toast.success(t("portal.form.toast_submitted", { id: created.id }));
      navigate("/portal/my-requests");
    } catch (e) {
      const det = e?.response?.data?.detail;
      const msg = typeof det === "string" ? det : (det?.message || e?.message || "Failed to submit");
      toast.error(msg);
    }
  };

  const send = async () => {
    const text = input.trim();
    if (!text || chat.isPending) return;
    setInput("");
    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    try {
      const resp = await chat.mutateAsync({
        messages: next.map((m) => ({ role: m.role, content: m.content })),
        locale,
      });
      setMessages((prev) => [...prev, { role: "assistant", content: resp.assistant_message || "…" }]);
      setExtracted(resp.extracted || {});
      setMissing(resp.missing || []);
      setMinimumMet(!!resp.minimum_met);
      if (resp.force_create) await _createWith(resp.extracted || {});
    } catch (_) {
      toast.error(t("portal.chat.err_temp"));
    }
  };

  const reset = () => {
    setMessages(intro.data?.intro ? [{ role: "assistant", content: intro.data.intro }] : []);
    setExtracted({}); setMissing([]); setMinimumMet(false); setInput("");
  };

  return (
    <div data-testid="portal-chat-page">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate("/portal/my-requests")}
            className="inline-flex items-center gap-1 px-2 py-1 rounded"
            style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.15)", ...mono(11, "var(--text-dim, #c5cad8)"), cursor: "pointer" }}
            data-testid="portal-chat-back"
          >
            <ArrowLeft size={13} /> {t("portal.chat.back")}
          </button>
          <h1 style={{ fontFamily: "Syne, sans-serif", fontWeight: 800, fontSize: 22, display: "flex", alignItems: "center", gap: 8, color: "var(--text, #f0f2f8)" }}>
            <Sparkles size={20} style={{ color: "#c89adb" }} /> {t("portal.chat.title")}
          </h1>
        </div>
        <button
          type="button"
          onClick={reset}
          className="inline-flex items-center gap-1 px-2 py-1 rounded"
          style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.15)", ...mono(11, "var(--text-dim, #c5cad8)"), cursor: "pointer" }}
          data-testid="portal-chat-reset"
        >
          <RotateCw size={12} /> {t("portal.chat.reset")}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
        <div
          className="rounded flex flex-col"
          style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(155,89,182,0.25)", height: "calc(100vh - 240px)", minHeight: 460 }}
        >
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="portal-chat-log">
            {messages.map((m, i) => (
              <div key={i} className="flex" style={{ justifyContent: m.role === "user" ? "flex-end" : "flex-start" }}>
                <div
                  className="rounded-lg px-3 py-2"
                  style={{
                    maxWidth: "78%",
                    background: m.role === "user" ? "#7e3aa8" : "rgba(126,58,168,0.12)",
                    color: m.role === "user" ? "#fff" : "var(--text, #f0f2f8)",
                    border: m.role === "assistant" ? "1px solid rgba(126,58,168,0.30)" : "none",
                    fontFamily: "'DM Mono', monospace",
                    fontSize: 12,
                    whiteSpace: "pre-wrap",
                    lineHeight: 1.45,
                  }}
                  data-testid={`portal-chat-msg-${m.role}-${i}`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {chat.isPending && (
              <div style={mono(10, "var(--text-dim, #9aa1b3)")}>{t("portal.chat.thinking")}</div>
            )}
          </div>
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="flex gap-2 p-3"
            style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t("portal.chat.title")}
              className="flex-1 rounded px-3 py-2"
              style={{
                background: "var(--bg, #0a0c14)", border: "1px solid rgba(255,255,255,0.10)",
                color: "var(--text, #f0f2f8)", fontFamily: "'DM Mono', monospace", fontSize: 12,
              }}
              data-testid="portal-chat-input"
            />
            <button
              type="submit"
              disabled={chat.isPending || !input.trim()}
              className="px-3 py-2 rounded inline-flex items-center gap-1"
              style={{
                background: "#7e3aa8", color: "#fff", border: "none",
                ...mono(11, "#fff", 700, "0.8px"), textTransform: "uppercase",
                opacity: (chat.isPending || !input.trim()) ? 0.6 : 1,
              }}
              data-testid="portal-chat-send"
            >
              <Send size={12} /> {t("portal.chat.send")}
            </button>
          </form>
        </div>

        <aside
          className="rounded p-4 space-y-3"
          style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(255,255,255,0.10)" }}
          data-testid="portal-chat-summary"
        >
          <div style={{ ...mono(10, "var(--text-dim, #c5cad8)", 700, "1.2px"), textTransform: "uppercase" }}>
            {t("portal.chat.summary_title")}
          </div>
          {Object.keys(FIELD_LABEL_KEYS).map((k) => {
            const v = extracted[k];
            const filled = v != null && v !== "" && !(typeof v === "object" && Object.values(v).every((x) => !x));
            return (
              <div key={k} className="flex items-start justify-between gap-3" data-testid={`portal-chat-field-${k}`}>
                <div style={mono(10, "var(--text-dim, #9aa1b3)", 600, "0.6px")}>{t(FIELD_LABEL_KEYS[k])}</div>
                <div style={{ ...mono(11, filled ? "#4ed084" : "var(--text-dim, #6b7388)"), maxWidth: 180, textAlign: "right", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {_fmt(k, v)}
                </div>
              </div>
            );
          })}
          <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", marginTop: 8, paddingTop: 8 }}>
            {minimumMet ? (
              <button
                type="button"
                onClick={() => _createWith(extracted)}
                disabled={createDraft.isPending}
                className="w-full px-3 py-2 rounded inline-flex items-center justify-center gap-2"
                style={{
                  background: "#4ed084", color: "#08090d", border: "none",
                  ...mono(11, "#08090d", 700, "0.8px"), textTransform: "uppercase",
                  opacity: createDraft.isPending ? 0.6 : 1, cursor: "pointer",
                }}
                data-testid="portal-chat-create-btn"
              >
                <Check size={13} /> {createDraft.isPending ? t("portal.form.submitting") : t("portal.chat.submit_btn")}
              </button>
            ) : (
              <div
                className="rounded px-2 py-1.5"
                style={{ background: "rgba(243,177,74,0.10)", color: "#f3b14a", border: "1px solid rgba(243,177,74,0.30)", ...mono(10, "#f3b14a"), textAlign: "center" }}
                data-testid="portal-chat-min-missing"
              >
                {t("portal.chat.min_missing")}
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
